import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Truck, Lock, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { createOrder } from "../lib/api";
import { BRAND, PRICING } from "../data/content";
import { toast } from "sonner";

const INITIAL = {
  name: "",
  mobile: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  pincode: "",
  landmark: "",
};

const Checkout = () => {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState(INITIAL);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (items.length === 0) {
      // give user a chance to land here cleanly
      const t = setTimeout(() => navigate("/"), 50);
      return () => clearTimeout(t);
    }
  }, [items, navigate]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    const mobileDigits = form.mobile.replace(/\D/g, "");
    if (mobileDigits.length < 10) e.mobile = "Valid 10-digit mobile required";
    if (!form.address_line_1.trim()) e.address_line_1 = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state.trim()) e.state = "State is required";
    if (form.pincode.replace(/\D/g, "").length !== 6) e.pincode = "6-digit pincode";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields");
      return;
    }

    setSubmitting(true);
    try {
      const order = await createOrder({
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
        shipping: form,
        notes,
      });
      clear();
      navigate(`/order/${order.order_number}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="font-body max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Your cart is empty</h1>
        <p className="mt-3 text-[#7F8C8D]">Redirecting to home…</p>
      </div>
    );
  }

  return (
    <div className="font-body bg-[#FFFBF5]">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-sm uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-3">
          Checkout
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">
          Almost there. Just your details.
        </h1>
        <p className="mt-2 text-[#7F8C8D]">
          Cash on Delivery — pay only when your order arrives.
        </p>

        {/* Alternative — order on WhatsApp */}
        <a
          data-testid="checkout-whatsapp-order"
          href={`https://wa.me/${BRAND.phoneDigits}?text=${encodeURIComponent(
            "🛒 Hi Gruham, I'd like to place an order for the Kitchen Platform Fan. Please help me complete my order."
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-full h-12 sm:h-14 px-6 sm:px-7 bg-[#25D366] text-white font-semibold hover:opacity-95 shadow-md shadow-emerald-800/20 transition"
        >
          <span className="text-lg">🛒</span>
          <span>Order on WhatsApp</span>
          <MessageCircle className="w-4 h-4" />
        </a>

        <p className="mt-2 text-xs text-[#7F8C8D]">
          Prefer to talk first? Place your order over WhatsApp — we'll confirm
          everything.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-10 grid lg:grid-cols-12 gap-8"
          data-testid="checkout-form"
        >
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white rounded-2xl border border-[#F39C12]/20 p-6">
              <h2 className="font-display text-xl font-semibold mb-5">
                Contact details
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">
                    Full name <span className="text-[#D63031]">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="checkout-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="e.g. Asha Sharma"
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                  {errors.name && (
                    <p className="text-xs text-[#D63031] mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="mobile">
                    Mobile number <span className="text-[#D63031]">*</span>
                  </Label>
                  <Input
                    id="mobile"
                    data-testid="checkout-mobile"
                    value={form.mobile}
                    onChange={(e) =>
                      update("mobile", e.target.value.replace(/[^\d+ ]/g, ""))
                    }
                    placeholder="10-digit mobile"
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                  {errors.mobile && (
                    <p className="text-xs text-[#D63031] mt-1">
                      {errors.mobile}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#F39C12]/20 p-6">
              <h2 className="font-display text-xl font-semibold mb-5">
                Shipping address
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="addr1">
                    Address line 1 <span className="text-[#D63031]">*</span>
                  </Label>
                  <Input
                    id="addr1"
                    data-testid="checkout-address1"
                    value={form.address_line_1}
                    onChange={(e) => update("address_line_1", e.target.value)}
                    placeholder="House / Flat / Block / Street"
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                  {errors.address_line_1 && (
                    <p className="text-xs text-[#D63031] mt-1">
                      {errors.address_line_1}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="addr2">
                    Address line 2 (optional)
                  </Label>
                  <Input
                    id="addr2"
                    data-testid="checkout-address2"
                    value={form.address_line_2}
                    onChange={(e) => update("address_line_2", e.target.value)}
                    placeholder="Area / Colony"
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                </div>
                <div>
                  <Label htmlFor="city">
                    City <span className="text-[#D63031]">*</span>
                  </Label>
                  <Input
                    id="city"
                    data-testid="checkout-city"
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                  {errors.city && (
                    <p className="text-xs text-[#D63031] mt-1">{errors.city}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="state">
                    State <span className="text-[#D63031]">*</span>
                  </Label>
                  <Input
                    id="state"
                    data-testid="checkout-state"
                    value={form.state}
                    onChange={(e) => update("state", e.target.value)}
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                  {errors.state && (
                    <p className="text-xs text-[#D63031] mt-1">
                      {errors.state}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="pincode">
                    Pincode <span className="text-[#D63031]">*</span>
                  </Label>
                  <Input
                    id="pincode"
                    data-testid="checkout-pincode"
                    value={form.pincode}
                    onChange={(e) =>
                      update(
                        "pincode",
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    placeholder="6-digit"
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                  {errors.pincode && (
                    <p className="text-xs text-[#D63031] mt-1">
                      {errors.pincode}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="landmark">
                    Landmark (optional)
                  </Label>
                  <Input
                    id="landmark"
                    data-testid="checkout-landmark"
                    value={form.landmark}
                    onChange={(e) => update("landmark", e.target.value)}
                    className="mt-1 h-11 bg-[#FFFBF5]"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#F39C12]/20 p-6">
              <h2 className="font-display text-xl font-semibold mb-3">
                Order notes (optional)
              </h2>
              <Textarea
                data-testid="checkout-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any preferred delivery time or instructions?"
                className="bg-[#FFFBF5]"
                rows={3}
              />
            </div>

            <div className="bg-white rounded-2xl border border-[#F39C12]/20 p-6">
              <h2 className="font-display text-xl font-semibold mb-3">
                Payment method
              </h2>
              <div className="flex items-center justify-between p-4 rounded-xl bg-[#FFF5E1] border border-[#F39C12]/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#D63031]" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-[#2C3E50]">
                      Cash on Delivery
                    </p>
                    <p className="text-xs text-[#7F8C8D]">
                      Pay only when your order arrives at your doorstep.
                    </p>
                  </div>
                </div>
                <span className="text-xs uppercase font-semibold tracking-widest text-[#27AE60]">
                  Selected
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-[#F39C12]/20 p-6">
              <h2 className="font-display text-xl font-semibold mb-4">
                Order summary
              </h2>
              <ul className="space-y-3">
                {items.map((i) => (
                  <li
                    key={i.product_id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-[#2C3E50]">
                      {i.name}{" "}
                      <span className="text-[#7F8C8D]">× {i.quantity}</span>
                    </span>
                    <span className="font-semibold">
                      ₹{(i.price * i.quantity).toLocaleString("en-IN")}
                    </span>
                  </li>
                ))}
              </ul>

              {(() => {
                const totalQty = items.reduce((s, i) => s + i.quantity, 0);
                const mrpTotal = PRICING.mrp * totalQty;
                const paidTotal = subtotal;
                const saved = Math.max(mrpTotal - paidTotal, 0);
                const shippingFee = 0;
                const grandTotal = paidTotal + shippingFee;
                return (
                  <div className="mt-5 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[#7F8C8D]">MRP total</span>
                      <span
                        data-testid="summary-mrp"
                        className="text-[#7F8C8D] line-through"
                      >
                        ₹{mrpTotal.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#7F8C8D]">Discounted price</span>
                      <span
                        data-testid="summary-price"
                        className="font-semibold text-[#2C3E50]"
                      >
                        ₹{paidTotal.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[#27AE60] font-medium">
                      <span>You save</span>
                      <span data-testid="summary-savings">
                        − ₹{saved.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[#27AE60] font-medium">
                      <span>Shipping</span>
                      <span data-testid="summary-shipping">FREE</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-[#F39C12]/15 pt-3 mt-2">
                      <span className="font-display font-semibold">Total (COD)</span>
                      <span
                        data-testid="checkout-total"
                        className="font-display text-2xl font-bold text-[#D63031]"
                      >
                        ₹{grandTotal.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <Button
                type="submit"
                data-testid="place-order-btn"
                disabled={submitting}
                className="cta-red rounded-full h-14 w-full mt-5 text-base font-semibold"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Placing…
                  </span>
                ) : (
                  "Place Order — Cash on Delivery"
                )}
              </Button>

              <div className="mt-5 grid grid-cols-3 gap-2 text-[11px] text-[#7F8C8D]">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#27AE60]" /> 1Y
                  warranty
                </span>
                <span className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-[#27AE60]" /> Secure data
                </span>
                <span className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-[#27AE60]" /> Free shipping
                </span>
              </div>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Checkout;
